import { Component, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import SocialRoomSet from 'entity/SocialRoomSet';
import StartingPoint from 'entity/StartingPoint';
import EntryOptionType from 'enums/EntryOptionType';

import { SceneService } from '../../services/scene.service';
import { SocialPageService } from '../../social-page/services/social-page.service';
import { ExtendedISnapZoomOptions } from '../../social-pixi-canvas/scene-manager';
import {
  PRESENTATION_V3_TIMEOUTS,
  PresentationTransitions,
  socialAppActions,
  socialAppSelectors,
} from '../../state/app';

import { Easing, easings } from './easings';

@Component({
  selector: 'room-settings-debugger',
  templateUrl: './room-settings-debugger.component.html',
  styleUrls: ['./room-settings-debugger.component.scss'],
  standalone: false,
})
export class RoomSettingsDebuggerComponent extends Loggable implements OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private socialPageService = inject(SocialPageService);
  protected sceneService = inject(SceneService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  public settings: Record<string, unknown> = {};
  public isRoomOpen = false;
  public EntryOptionType = EntryOptionType;
  public entryOption = EntryOptionType.DEFAULT;
  public roomSet?: SocialRoomSet;
  public groupFormerCount = 0;
  public startingPoint: StartingPoint | undefined;
  public startingPointId?: string;
  protected presenterCount = 1;

  // auto-zoom
  public snapZoomOptions: ExtendedISnapZoomOptions | undefined;
  public easings: Easing[] = easings;

  public presentationIconFadeIn: number;
  public backgroundFadeOutTimeout: number;
  public backgroundFadeInTimeout: number;
  public audioFadeTimeout: number;
  public transitionLoadTimeout: number;

  public constructor() {
    super();

    this.setDisplayName('WaitingRoomDebuggerComponent');

    ({
      audioFadeTimeout: this.audioFadeTimeout,
      backgroundFadeInTimeout: this.backgroundFadeInTimeout,
      backgroundFadeOutTimeout: this.backgroundFadeOutTimeout,
      presentationIconFadeIn: this.presentationIconFadeIn,
      transitionLoadTimeout: this.transitionLoadTimeout,
    } = PRESENTATION_V3_TIMEOUTS);

    this.store
      .select(socialAppSelectors.getTransitionTimers)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((transitionTimers) => {
        ({
          audioFadeTimeout: this.audioFadeTimeout,
          backgroundFadeInTimeout: this.backgroundFadeInTimeout,
          backgroundFadeOutTimeout: this.backgroundFadeOutTimeout,
          presentationIconFadeIn: this.presentationIconFadeIn,
          transitionLoadTimeout: this.transitionLoadTimeout,
        } = transitionTimers);
      });

    this.store
      .select(socialAppSelectors.getRoomSettings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.settings = val?.settings ?? {};
      });

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((set) => {
        this.roomSet = set;
        this.groupFormerCount = set.groupFormerCount ?? 0;
        this.entryOption = set.entryOption ?? EntryOptionType.DEFAULT;
        if (set.socialRoomSetId) {
          this.socialPageService
            .getStartingPoint(set.socialRoomSetId)
            .then((startingPoint) => {
              if (!startingPoint) {
                return;
              }
              this.startingPoint = startingPoint;
              this.startingPointId = startingPoint.socialId;
            })
            .catch((err) => {
              this.error(err);
            });
        }
      });

    this.store
      .select(socialAppSelectors.isRoomOpen)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isOpen) => (this.isRoomOpen = isOpen));

    this.store
      .select(socialAppSelectors.getStartingPoint)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((startingPoint) => (this.startingPoint = startingPoint));

    this.snapZoomOptions = this.sceneService?.sceneManager?.snapZoomOptions;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  protected updatePresenterCount() {
    // TODO: potential memory leak due to singleton
    // PresentationPresenterVideosComponent.getInstance().updateTest(this.presenterCount);
  }

  public updateSnapZoomOptions() {
    if (!this.sceneService.sceneManager) {
      return;
    }
    this.logUserAction('updateSnapZoomOptions', 'change');
    this.sceneService.sceneManager.snapZoomOptions = { ...this.snapZoomOptions };
  }

  public updateStartingPoint() {
    if (this.roomSet && this.startingPointId && this.roomSet.socialRoomSetId) {
      this.logUserAction('updateStartingPoint', 'click');
      this.store.dispatch(
        socialAppActions.UPDATE_STARTING_POINT_REQ({
          socialRoomSetId: this.roomSet.socialRoomSetId,
          startingPoint: { ...this.startingPoint, socialId: this.startingPointId },
        }),
      );
    }
  }

  /**
   * Returns true if the entry settings don't match the current room set
   */
  public get entrySettingsDirty(): boolean {
    return !(
      this.roomSet?.entryOption === this.entryOption && this.roomSet?.groupFormerCount === this.groupFormerCount
    );
  }

  /**
   * Saves the entry settings to the room set
   */
  public onSaveEntryConfiguration() {
    this.logUserAction('onSaveEntryConfiguration', 'click');
    const updatedSet: SocialRoomSet = {
      ...this.roomSet,
      entryOption: this.entryOption,
      groupFormerCount: this.groupFormerCount,
    };

    this.store.dispatch(socialAppActions.UPDATE_ROOM_SET_REQ({ roomSet: updatedSet }));
  }

  /**
   * Save the transition timers for the room set
   */
  public onSaveTransitionTimers() {
    this.logUserAction('onSaveTransitionTimers', 'click');
    const updatedTimers: PresentationTransitions = {
      audioFadeTimeout: this.audioFadeTimeout,
      backgroundFadeInTimeout: this.backgroundFadeInTimeout,
      backgroundFadeOutTimeout: this.backgroundFadeOutTimeout,
      presentationIconFadeIn: this.presentationIconFadeIn,
      transitionLoadTimeout: this.transitionLoadTimeout,
    };

    this.store.dispatch(socialAppActions.UPDATE_TRANSITION_TIMERS({ transitionTimers: updatedTimers }));
  }

  public mapRoomSetting(setting: string, value: unknown): unknown {
    switch (setting) {
      // case ENTRY_FLOW: {
      //   const entryOption = Number(value) as EntryOptionType;
      //   switch (entryOption) {
      //     case EntryOptionType.ACCOUNT:
      //       return `Account: (${entryOption})`;
      //     case EntryOptionType.CUSTOM_RULES:
      //       return `Custom Rules: (${entryOption})`;
      //     case EntryOptionType.DEFAULT:
      //       return `Default: (${entryOption})`;
      //     case EntryOptionType.STARTING_POINT:
      //       return `Starting Point: (${entryOption})`;
      //     default:
      //       assertNever(entryOption);
      //   }
      //   break;
      // }
      default:
        return value;
    }
  }
}
